import {
  ApolloClient,
  ApolloLink,
  FetchPolicy,
  gql,
  InMemoryCache,
  OperationVariables,
  TypedDocumentNode,
  HttpLink,
  CombinedGraphQLErrors,
  ServerError,
} from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import { SetContextLink } from '@apollo/client/link/context';
import { DocumentNode } from 'graphql';
import { errorMessages } from '@/utils/errorMessages';
import { getLocalStorageValue, isRouteProtected, localStorageKey, logout, showToast } from '@/utils/utils';

// Server-side message returned when a mutation hits a per-action FORBIDDEN.
// Exported so reducers that both consume handleError's thrown message AND
// run their own toast on `.rejected` can skip to avoid a duplicate toast.
export const UNAUTHORIZED_ACTION_MESSAGE = 'You don\'t have permission to perform this action';

// Shared suppression helper for thunk `.rejected` handlers. handleError toasts
// errorMessages.unauthorized for per-action FORBIDDEN; without this guard every
// reducer's generic error toast stacks on top.
export const showRejectedToast = (
  action: { error?: { message?: string } },
  errorMessageKey: string,
) => {
  if (action.error?.message === UNAUTHORIZED_ACTION_MESSAGE) return;
  showToast(errorMessageKey, 'error');
};

// Builder helper to register a `.rejected` handler that uses showRejectedToast.
// Collapses the common `builder.addCase(x.rejected, (_, action) => showRejectedToast(action, key))`
// pattern across reducers into a single call, which avoids near-identical
// duplicated blocks and keeps the suppression consistent.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const addRejectedToast = <T extends { rejected: any }>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builder: { addCase: (matcher: any, reducer: (state: any, action: any) => void) => any },
  thunk: T,
  errorMessageKey: string,
) => builder.addCase(thunk.rejected, (_: unknown, action: { error?: { message?: string } }) => {
  showRejectedToast(action, errorMessageKey);
});

const getInitialGraphqlUrl = () => {
  // Set NEXT_PUBLIC_IS_LOCAL_APP to the explicit string 'true' at build time to enable
  // the local GraphQL URL override for development and testing client bundles.
  if (process.env.NEXT_PUBLIC_IS_LOCAL_APP === 'true' && process.env.NEXT_PUBLIC_GRAPHQL_URL) {
    return process.env.NEXT_PUBLIC_GRAPHQL_URL;
  }

  return '/api';
};

// Runtime GraphQL URL cache, populated by _app.tsx on mount
let _graphqlUrl = getInitialGraphqlUrl();

export function setGraphqlUrl(url: string) {
  _graphqlUrl = url;
}

// Create an HTTP Link — lazy URI function reads the runtime config
const httpLink = new HttpLink({
  uri: () => _graphqlUrl,
});

// Add Authorization Header
const authLink = new SetContextLink(({ headers }) => {
  const token = getLocalStorageValue(localStorageKey.wovenToken);
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Flag to prevent multiple concurrent logout operations
let isLoggingOut = false;

// Error handling link - intercepts all GraphQL/network errors
const errorLink = new ErrorLink(({ error }) => {
  const path = globalThis?.location?.pathname;
  let isAuthError = false;

  // Check for network errors with 401 status
  if (ServerError.is(error) && error.statusCode === 401) {
    isAuthError = true;
  }

  // Check for GraphQL errors indicating authentication failure
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) => {
      if (message === 'Not authenticated' || extensions?.code === 'UNAUTHENTICATED') {
        isAuthError = true;
      }
    });
  }

  // Handle authentication errors - redirect to login (only once)
  if (isAuthError && !isLoggingOut) {
    isLoggingOut = true;
    if (isRouteProtected(path)) {
      showToast(errorMessages.notAuthenticated, 'error');
    }
    logout();
    // Clear Apollo cache and redirect - fire and forget since page reload resets everything
    client.clearStore().catch(() => { }).finally(() => {
      globalThis.location.replace('/login');
    });
  }
});

// Combine authLink, errorLink, and httpLink - single Apollo client instance for the entire app
const client = new ApolloClient({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  // Preserve Apollo Client 3 behavior: notifyOnNetworkStatusChange defaults to true in v4,
  // which would cause loading=true on every refetch() call (e.g. after mutations).
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: false,
    },
  },
});

/**
 * Helper function for making GraphQL requests
 * @param {string} query - GraphQL query or mutation string
 * @param {Record<string, unknown>} [variables] - Variables for the GraphQL operation
 * @returns {Promise<unknown>} - The response data
 */
export const graphqlRequest = async <TVariables extends OperationVariables = OperationVariables, TResult = unknown>(
  query: TypedDocumentNode<TResult, TVariables> | DocumentNode | string,
  variables?: TVariables,
  fetchPolicy: FetchPolicy = 'network-only',
): Promise<TResult> => {
  try {
    const response = await client.query({
      query: typeof query === 'string' ? gql(query) : query,
      variables,
      fetchPolicy,
    });

    return response.data as TResult;
  } catch (error) {
    throw new Error(handleError(error), { cause: error });
  }
};

export const graphqlMutation = async <TVariables extends OperationVariables = OperationVariables, TResult = unknown>(
  mutation: TypedDocumentNode<TResult, TVariables> | DocumentNode,
  variables?: TVariables,
): Promise<TResult> => {
  try {
    const response = await client.mutate({
      mutation: mutation as DocumentNode,
      variables,
    });

    return response.data as TResult;
  } catch (error) {
    throw new Error(handleError(error), { cause: error });
  }
};

type ErrorLike = { message?: string } | undefined;

export const handleError = (error: unknown): string => {
  // If logout is already in progress, return empty string to avoid duplicate handling
  if (isLoggingOut) {
    return '';
  }

  const path = globalThis?.location?.pathname;

  // Extract error message for forms and UI display
  let errorMessage: string;

  if (CombinedGraphQLErrors.is(error)) {
    errorMessage = error.errors[0]?.message ?? errorMessages.unknownError;
  } else {
    errorMessage = (error as ErrorLike)?.message ?? (typeof error === 'string' ? error : errorMessages.unknownError);
  }

  // Handle authorization errors (different from authentication):
  // Surface the failure via a toast but leave the user on the current page.
  // A blanket redirect to /projects here (previously the case) is destructive:
  // - it discards in-flight work and navigation history for a single-action
  //   permission failure (e.g. a mutation the current role cannot perform),
  // - it triggers a cascade redirect for single-project non-admin roles who
  //   land on /projects and get bounced to their contributor dashboard,
  // - it hides the real error from the caller. See woven-frontend #1152.
  if (errorMessage === UNAUTHORIZED_ACTION_MESSAGE && isRouteProtected(path)) {
    showToast(errorMessages.unauthorized, 'error');
    return errorMessage;
  }

  // Return error message for form validation and UI display
  return errorMessage;
};

export default client;
